import { useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { connectToHubMesas } from "src/redux/slices/hub";
import {
  receiveMesasFromHub,
  receiveMesaFromHub,
  deleteMesaSuccess,
  createMesaSuccess,
  cleanMesaAndProducts,
  updateMesaFromHub,
  getMesas,
} from "src/redux/slices/mesas";
import buildHubUrl from "./buildHubUrl";
import CreateConnection from "./HubMatrix";
import { addReconnectListener } from "./HubConnectionManager";
import { leaseEventReceived } from "src/redux/slices/mesaLeases";
import { receiveLastPayment } from "src/redux/slices/session";
import { lastPaymentFromEvent } from "../mesaLeaseCore";
import { getDeviceId } from "../deviceId";
import {
  bindTpvResyncStore,
  requestSelectedCheckout,
  resyncTpvState,
} from "../tpvResync";
import axios from "../axios";
import { fetchComensales, SyncCart } from "src/redux/slices/product";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { PATH_APP } from "src/routes/paths";
import { postActionRoute } from "src/utils/postActionRoute";
import { showNotificationBoundToThisTab } from "../notifications";
import {
  isRecentLocalFetch,
  isRecentLocalDinerSplitAction,
} from "../localCheckoutFetchTracker";
import { noteMesaChanged } from "../mesaCheckoutLoader";

// La URL del hub se construye dinámicamente con buildHubUrl para pasar el
// restauranteId como query — el backend lo usa en OnConnectedAsync para unir
// la conexión al grupo "restaurante-{id}" y recibir solo los broadcasts de ESE
// restaurante (antes: Clients.All cross-tenant).

export default function useHubMesas() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.authJwt.user);
  // Selectores granulares: suscribirse a todo `state.mesas` repintaba el
  // layout (TopBar/NavBar) en cada cambio de cualquier mesa.
  const selectedId = useSelector((state) => state.mesas.selectedId);
  const mesaHub = useSelector((state) => state.mesas.mesaHub);

  const history = useHistory();

  const store = useStore();
  const mesaRef = useRef(null);
  // Guardia de doble-conexión LOCAL al hook (no Redux): el flag de Redux
  // nunca se reseteaba al cerrar el websocket y tras un remount mesahub no
  // volvía a conectarse hasta recargar.
  const isConnectedRef = useRef(false);

  useEffect(() => {
    bindTpvResyncStore(store.dispatch, store.getState);
  }, [store]);

  useEffect(() => {
    mesaRef.current = selectedId;
  }, [selectedId]);

  // Fase 3 · Evento `mesa/{rid}`: la ficha ya se parcheó con el payload
  // (receiveMesaFromHub). Para la mesa SELECCIONADA solo se refresca el
  // checkout, coalescido: un evento que llega con un fetch en vuelo
  // programa UNO más (antes se descartaba y se perdían cambios de otro
  // dispositivo) y ya no se pide el plano entero ni getOrdersBySession.
  useEffect(() => {
    if (!mesaHub) return;

    // Cualquier cambio de la mesa invalida su cuenta guardada (mesa
    // cobrándose; ver mesaCheckoutLoader → takeHeldCheckout).
    noteMesaChanged(mesaHub.id);

    // Eco de una acción de ESTE dispositivo (click, comandar, cobrar…): el
    // estado local ya está fresco.
    if (isRecentLocalFetch(mesaHub.id, 2000)) {
      dispatch(updateMesaFromHub(null));
      return;
    }

    if (String(mesaHub.id) === String(mesaRef.current)) {
      if (!mesaHub.ocupada) {
        dispatch(cleanMesaAndProducts());
        const currentPath =
          history.location?.pathname || window.location.pathname;
        if (currentPath.startsWith(PATH_APP.tpv.cartaCamarero)) {
          history.push(postActionRoute(user));
        }
      } else {
        requestSelectedCheckout(mesaHub.id);
      }
    }

    dispatch(updateMesaFromHub(null));
  }, [mesaHub]);

  useEffect(() => {
    if (!user?.id || isConnectedRef.current) return;

    isConnectedRef.current = true;

    const mesaHubUrl = buildHubUrl("/hub/mesahub", { restauranteId: user.id });
    const cleanup = CreateConnection(
      mesaHubUrl,
      [
        // Fase 1 TPV · presencia/lease de mesa. El reducer detecta si el
        // evento me quita una mesa mía (takeover) y lo deja en
        // `mesaLeases.revoked` para que el bridge saque al camarero.
        {
          id: `mesa/lease/${user.id}`,
          operator: (evt) =>
            dispatch(leaseEventReceived({ evt, myDeviceId: getDeviceId() })),
        },
        // Fase 1 TPV · último cobro en TODOS los dispositivos.
        {
          id: `payment/${user.id}`,
          operator: (evt) =>
            dispatch(receiveLastPayment(lastPaymentFromEvent(evt, getDeviceId()))),
        },
        {
          id: `mesa/${user.id}`,
          operator: (mesa) => {
            dispatch(receiveMesaFromHub(mesa));
            dispatch(updateMesaFromHub(mesa));
          },
        },
        {
          id: `mesa/updatestateofproducts/${user.id}`,
          operator: async (mesa) => {
            const currentPath =
              history.location?.pathname || window.location.pathname;

            if (!currentPath.startsWith(PATH_APP.tuCarta.tpv)) return;

            var text = "Los productos ya están listos.";
            if (mesa?.nombre) {
              text += ` Mesa: ${mesa.nombre}.`;
            } else {
              text += ` Mesa ID: ${mesa.id_mesa}.`;
            }

            if (mesa?.zonaName) {
              text += ` Zona: ${mesa.zonaName}.`;
            }

            await showNotificationBoundToThisTab({
              title: `Mesa ${mesa.nombre || mesa.id_mesa} - ${mesa.zonaName || "Sin zona"} actualizada`,
              body: text,
              url: `/app/tucarta/tpv`, // o la ruta que te interese abrir
              tag: `mesa-kitchen-tpv-${user.id}`,
            });
          },
        },
        {
          id: `mesas/sync/${user.id}`,
          // El reducer conserva la identidad de las mesas sin cambios.
          operator: () => {
            dispatch(getMesas({ fresh: true }));
          },
        },
        {
          id: `mesas/all/${user.id}`,
          operator: (mesas) => dispatch(receiveMesasFromHub(mesas)),
        },
        {
          id: `mesa/delete/${user.id}`,
          operator: (idMesa) => dispatch(deleteMesaSuccess(idMesa)),
        },
        {
          id: `mesa/create/${user.id}`,
          operator: (mesa) => dispatch(createMesaSuccess(mesa)),
        },
        // 2026-09-03 · Split-by-comensal (Hiopos-like). El backend
        // emite `dinerSplit/{restauranteId}` tras Add/Rename/Delete
        // comensal y tras mover productos. Todos los dispositivos del
        // restaurante refetch los comensales de la sesión afectada.
        // El checkout se refetch SOLO si es la sesión activa de este
        // dispositivo (evita refetch cross-mesa innecesarios).
        {
          id: `dinerSplit/${user.id}`,
          operator: (payload) => {
            const idSession = payload?.idSession;
            if (!idSession) return;
            // 2026-09-03 · Dedupe rebound del propio dispositivo.
            // Los thunks `addComensal/renameComensal/deleteComensal/
            // moverProductosComensal*` marcan `markLocalDinerSplitAction`
            // justo antes del axios call. Si el evento del hub llega
            // dentro de la ventana, es el eco de la propia acción — el
            // optimismo local ya se aplicó y la respuesta HTTP trae los
            // datos frescos. Refetch en ese caso solo añade latencia y
            // sobrescribe el cart local con race conditions.
            if (isRecentLocalDinerSplitAction(idSession)) {
              return;
            }
            dispatch(fetchComensales(idSession));
            // Fase 3 · Solo la mesa de esa sesión (nº de comensales), no el
            // plano entero en todos los dispositivos.
            dispatch((_, getState) => {
              const target = (getState()?.mesas?.mesas || []).find(
                (m) => m?.sessionId === idSession || m?.idSession === idSession,
              );
              if (target?.id) {
                axios
                  .get(`/mesas/${target.id}`)
                  .then((res) => {
                    if (res?.data?.mesa) dispatch(receiveMesaFromHub(res.data.mesa));
                  })
                  .catch(() => {});
              }
            });
            dispatch((_, getState) => {
              const activeMesa = getState()?.mesas?.mesa;
              const currentSession = activeMesa?.sessionId || activeMesa?.idSession;
              if (currentSession === idSession && activeMesa?.id) {
                dispatch(SyncCart(activeMesa.id, idSession));
              }
            });
          },
        },
      ],
      dispatch,
      connectToHubMesas,
      "mesa",
    );

    // Tras reconectar se han podido perder eventos: rehidratamos presencia
    // y último cobro (snapshot) en vez de fiarnos del estado en memoria.
    // Fase 3 · Resync único (mesas + checkout actual + presencia + último
    // cobro). El manager lo dispara también tras reinicios por onclose/watchdog.
    const unsubscribeReconnect = addReconnectListener(mesaHubUrl, () => {
      resyncTpvState("mesahub-reconnect");
    });

    return () => {
      unsubscribeReconnect();
      cleanup();
      isConnectedRef.current = false;
    };
  }, [user?.id]); // Eliminamos selectedId para evitar múltiples conexiones innecesarias
}
